import type { ComponentPropsWithoutRef } from "react"
import React from "react"
import { IconCircleQuestionOutline18 as HelpCircle } from "nucleo-ui-outline-18"

import { cn } from "@productlane/lib/src/cn"

import { BacklogIcon } from "./project-status/backlog-icon"
import { CanceledIcon } from "./project-status/canceled-icon"
import { CompletedIcon } from "./project-status/completed-icon"
import { PlannedIcon } from "./project-status/planned-icon"
import { StartedIcon } from "./project-status/started-icon"

export const ProjectStatusIconKeys = [
	"backlog",
	"canceled",
	"completed",
	"planned",
	"started",
	"closed",
] as const

export type ProjectStatusTypes = (typeof ProjectStatusIconKeys)[number]

export const ProjectStatusColors: Record<ProjectStatusTypes, string> = {
	backlog: "#bec2c8",
	planned: "#5e6ad2",
	started: "#f2c94c",
	completed: "#5e6ad2",
	canceled: "#95a2b3",
	closed: "#95a2b3",
}

export const ProjectStatusIcons: Record<
	ProjectStatusTypes,
	typeof BacklogIcon
> = {
	backlog: BacklogIcon,
	canceled: CanceledIcon,
	completed: CompletedIcon,
	planned: PlannedIcon,
	started: StartedIcon,
	closed: CanceledIcon,
}

interface ProjectStatusIconProps extends ComponentPropsWithoutRef<"svg"> {
	status: ProjectStatusTypes
	colored?: boolean
	customColor?: string
}

export const ProjectStatusIcon = React.memo(
	React.forwardRef<SVGSVGElement, ProjectStatusIconProps>(
		({ status, className, colored, customColor, ...props }, ref) => {
			const Icon = ProjectStatusIcons[status]
			if (!Icon) {
				return (
					<HelpCircle
						ref={ref}
						{...props}
						className={cn("flex-none", className)}
					/>
				)
			}

			const color =
				customColor ?? (colored ? ProjectStatusColors[status] : undefined)

			return (
				<Icon
					ref={ref}
					{...props}
					color={color}
					className={cn("flex-none", className)}
				/>
			)
		},
	),
)
